// src/Components/PresenceHost.jsx
//
// Montado una sola vez en el root de la app (ver App.jsx), junto a
// VideochatHost. Única responsabilidad: avisarle al relay quién es el
// usuario de este socket para que is_live (discover) refleje presencia
// real — no toca nada de señalización de llamadas, esa sigue siendo
// responsabilidad exclusiva de VideochatHost.
//
// Reusa el socket global ya existente (getVideochatSocket) en vez de abrir
// uno nuevo — el dueño de la conexión (connect/disconnect según sesión)
// sigue siendo VideochatHost; este componente solo agrega un listener más
// sobre el mismo socket. Ver docs/presencia-online-web-dashboard.md del
// backend para el contrato completo.
import { useEffect } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { authUserAtom, dataUserAtom, onlineStatusOverridesAtom } from '../Services/store/atoms'
import { getVideochatSocket } from '../Services/videochatSocket'

export default function PresenceHost() {
  const token = useAtomValue(authUserAtom)?.token
  const userId = useAtomValue(dataUserAtom)?.id
  const setOnlineStatusOverrides = useSetAtom(onlineStatusOverridesAtom)

  // Reemitir en cada reconexión (el server no recuerda la identidad de un
  // socket anterior). No hace falta heartbeat propio ni escuchar
  // visibilitychange: el relay ya refresca la presencia solo cada 60s
  // mientras el socket siga conectado, sin importar en qué pantalla del
  // sitio esté parado el usuario.
  useEffect(() => {
    if (!token || userId == null) return
    const socket = getVideochatSocket()
    if (!socket) return
    const identify = () => socket.emit('identify', userId)
    if (socket.connected) identify()
    socket.on('connect', identify)
    return () => socket.off('connect', identify)
  }, [token, userId])

  // Push en vivo del relay: cualquier pantalla que lea onlineStatusOverridesAtom
  // (ChatScreen, discover, etc.) refleja online/offline al instante, sin
  // esperar a que se vuelva a pedir el endpoint REST que trae is_live.
  useEffect(() => {
    if (!token) return
    const socket = getVideochatSocket()
    if (!socket) return
    const onPresenceChanged = ({ userId: changedId, status }) => {
      setOnlineStatusOverrides(prev => {
        const next = new Map(prev)
        next.set(String(changedId), status === 'online')
        return next
      })
    }
    socket.on('presence-changed', onPresenceChanged)
    return () => socket.off('presence-changed', onPresenceChanged)
  }, [token, setOnlineStatusOverrides])

  return null
}
